// Dev Mode's switch and its decisions, with nothing of the page in them (docs/features/dev-mode.md) — the page
// (index.js) and tools/tests/devcode.mjs both use these.
//
// The switch is this browser's own: kept in localStorage, off until the person turns it on in this page's ⋯ sheet,
// and never set by anything the Mac sends. It names the Mac it is for.
import './gate.js'

export const KEY = 'cody.devMode'

export const allowedHost = (host) => globalThis.codyDevCode.allowedHost(host)

/// { on: false } unless this browser turned it on (and for which Mac).
export function readSwitch(storage) {
  try {
    const v = JSON.parse(storage?.getItem(KEY) || 'null')
    if (v && v.on === true && typeof v.mac === 'string' && v.mac) return { on: true, mac: v.mac, macName: String(v.macName || 'the Mac') }
  } catch {}
  return { on: false }
}

export function writeSwitch(storage, value) {
  try { value?.on ? storage.setItem(KEY, JSON.stringify({ on: true, mac: value.mac, macName: value.macName || '' })) : storage.removeItem(KEY) } catch {}
}

/// `?dev=off` in the address turns it off (the way back when the dev copy will not even start).
export const urlSaysOff = (search) => { try { return new URLSearchParams(search || '').get('dev') === 'off' } catch { return false } }
export function withoutDevParam(href) {
  const url = new URL(href)
  url.searchParams.delete('dev')
  return url.pathname + (url.searchParams.toString() ? '?' + url.searchParams : '') + url.hash
}

/// May this Mac's offer be shown? Only on an allowed host, only from a Mac whose identity key this browser has
/// pinned (it signed the room's challenge: core/pairing.js judgeMac) and only when it said it serves.
export const mayOffer = ({ host, pinned, offer }) => allowedHost(host) && !!pinned && offer?.what === 'offer' && !!offer.source

/// The banner's words for an offer: which copy of the web app this is.
export function sourceText(offer, macName) {
  const mac = macName || 'the Mac'
  if (offer?.source === 'folder') return `Web app from folder ${offer.folder || '?'} on ${mac}`
  return `Web app from ${mac}’s app${offer?.version ? ' (' + offer.version + ')' : ''}`
}

/// The strip's words (Josh, 10-01: "a very small top banner that tells me it's in dev mode, and even which branch
/// I'm looking at"): from the signed list — `source` "app 0.29.1" | "folder ‹name›", `branch`, `commit`, `dirty`.
///   "DEV · gauntlet-grab @ 6b7fd88 · from Studio app 0.29.1" · "DEV · gauntlet-grab @ 6b7fd88+changes · folder xr on Studio"
export function stripText(info, macName) {
  const mac = macName || 'the Mac'
  const where = info?.branch || info?.commit ? `${info.branch || '?'} @ ${info.commit || '?'}${info.dirty ? '+changes' : ''}` : ''
  const source = String(info?.source || '')
  const from = source.startsWith('folder ') ? `${source} on ${mac}` : `from ${mac}${source ? ' ' + source : ''}`
  return ['DEV', where, from].filter(Boolean).join(' · ')
}

/// What the strip shows of a signed list.
export const signedInfo = (list) => ({ source: String(list?.source || ''), branch: String(list?.branch || ''), commit: String(list?.commit || ''), dirty: !!list?.dirty })

// MARK: what the page does, at each moment

/// On load. `servedDev`: the service worker served this page from the dev copy.
///   'reload'  the switch is off but this page is the dev copy: load the deployed one
///   'none'    otherwise
export const onLoad = ({ switchOn, servedDev }) => (!switchOn && servedDev ? 'reload' : 'none')

/// When the chosen Mac's offer arrives.
///   'sync'         bring the dev copy up to date (a dev page then reloads if anything changed)
///   'sync-reload'  a deployed page with the switch on: bring it up to date, then load it
///   'wait'         a deployed page that fell back a moment ago, or whose dev copy did not start: the banner's
///                  Retry decides, so a Mac that answers here but not there never loops the page; refused dev
///                  code (a bad signature or hash) also waits for Retry
///   'none'         not the Mac the switch is for, or the switch is off
export function onOffer({ switchOn, chosen, from, servedDev, fallback, now = Date.now() }) {
  if (!switchOn || from !== chosen) return 'none'
  if (servedDev) return 'sync'
  if (fallback?.why === 'did-not-start' || fallback?.why === 'refused') return 'wait'
  if (fallback && now - (fallback.at || 0) < 90000) return 'wait'
  return 'sync-reload'
}

/// Some seconds after a dev page loaded: no offer from its Mac yet means the Mac is unreachable — fall back to the
/// deployed copy and say so.
export const onTimeout = ({ switchOn, servedDev, offered }) => (switchOn && servedDev && !offered ? 'fallback' : 'none')
export const OFFER_WAIT = 25000

// MARK: the dev copy's files

const TEXT = /\.(js|mjs|css|html|json|md|svg|txt|webmanifest)$/i
/// What is fetched up front, so the dev copy can start before any Mac is connected: the text files (the scripts,
/// styles, pages), at most 1 MB each — models, sounds and pictures come when first asked for, through the page.
/// dist/ (a bundle) only when the copy's index.html loads it.
export function eagerPaths(files, indexHtml = '') {
  const bundled = /dist\/app\.js/.test(indexHtml)
  return Object.entries(files || {}).filter(([path, f]) => TEXT.test(path) && f.size <= 1024 * 1024 && (bundled || !path.startsWith('dist/'))).map(([path]) => path)
}

/// Old manifest → new: what to fetch again and what to drop. Files are {path: {size, mtime, sha256}}; the hash
/// decides when both have one.
export function manifestDiff(before = {}, after = {}) {
  const changed = [], removed = []
  for (const [path, f] of Object.entries(after)) { const b = before[path]; if (!b || (b.sha256 && f.sha256 ? b.sha256 !== f.sha256 : b.size !== f.size || b.mtime !== f.mtime)) changed.push(path) }
  for (const path of Object.keys(before)) if (!(path in after)) removed.push(path)
  return { changed, removed }
}

/// A path of the app as the Mac is asked for it (gate.js, shared with the service worker).
export const appPath = (pathname, base) => globalThis.codyDevCode.appPath(pathname, base)
